import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IHealthCheckRuleConfig,
  IHealthCheckRuleConfigType,
} from '../../Interfaces/IHealthCheckRule';
import { customFetcher } from '../useFetch';

/**
 * Konfiguration der Health-Check-Regeln (#1283).
 *
 * Bewusst getrennt von `HealthCheckQueries` — das ist der Dubletten-Cleanup und
 * eine andere Sache, die nur denselben Namen trägt.
 *
 * Beide Tabellen sind klein (eine Zeile je konfigurierter Regel), deshalb werden
 * sie vollständig geladen und im Frontend zusammengesetzt statt über ein
 * fullObject.
 */
const CONFIG_KEY = 'healthcheckruleconfig';
const CONFIG_TYPE_KEY = 'healthcheckruleconfigtype';

/** Nur die schreibbaren Felder senden; connexion erwartet echte Booleans. */
const mapConfigPayload = (config: Partial<IHealthCheckRuleConfig>) => ({
  RuleKey: config.RuleKey,
  IsActive: Boolean(config.IsActive),
  Severity: config.Severity ?? null,
  ScopeMode: config.ScopeMode ?? 'all',
  Params: config.Params ?? null,
});

/** `enabled: false` lädt nichts — für Benutzer ohne Health-Check-Freischaltung. */
export const useGetHealthCheckRuleConfigs = ({ enabled = true } = {}) => {
  const {
    data,
    isLoading: isLoadingHealthCheckRuleConfigs,
    refetch: refetchHealthCheckRuleConfigs,
  } = useQuery<IHealthCheckRuleConfig[]>({
    queryKey: [CONFIG_KEY],
    queryFn: () => customFetcher({ endpoint: '/healthcheckruleconfig' }),
    refetchOnWindowFocus: false,
    enabled,
  });

  return {
    healthCheckRuleConfigs: data ?? [],
    isLoadingHealthCheckRuleConfigs,
    refetchHealthCheckRuleConfigs,
  };
};

/** `enabled: false` lädt nichts — für Benutzer ohne Health-Check-Freischaltung. */
export const useGetHealthCheckRuleConfigTypes = ({ enabled = true } = {}) => {
  const {
    data,
    isLoading: isLoadingHealthCheckRuleConfigTypes,
    refetch: refetchHealthCheckRuleConfigTypes,
  } = useQuery<IHealthCheckRuleConfigType[]>({
    queryKey: [CONFIG_TYPE_KEY],
    queryFn: () => customFetcher({ endpoint: '/healthcheckruleconfigtype' }),
    refetchOnWindowFocus: false,
    enabled,
  });

  return {
    healthCheckRuleConfigTypes: data ?? [],
    isLoadingHealthCheckRuleConfigTypes,
    refetchHealthCheckRuleConfigTypes,
  };
};

export const useCreateHealthCheckRuleConfig = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createHealthCheckRuleConfig, isPending } = useMutation({
    mutationFn: (config: Partial<IHealthCheckRuleConfig>) =>
      customFetcher({
        endpoint: '/healthcheckruleconfig',
        method: 'POST',
        body: mapConfigPayload(config),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] });
    },
  });

  return {
    createHealthCheckRuleConfig,
    isPendingCreateHealthCheckRuleConfig: isPending,
  };
};

export const useUpdateHealthCheckRuleConfig = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateHealthCheckRuleConfig, isPending } = useMutation({
    mutationFn: (config: IHealthCheckRuleConfig) =>
      customFetcher({
        endpoint: '/healthcheckruleconfig',
        method: 'PUT',
        body: {
          idHealthCheckRuleConfig: config.idHealthCheckRuleConfig,
          ...mapConfigPayload(config),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] });
    },
  });

  return {
    updateHealthCheckRuleConfig,
    isPendingUpdateHealthCheckRuleConfig: isPending,
  };
};

/*
 * Bewusst kein Hook zum Löschen einer Konfigurationszeile.
 *
 * Ein „Regel auf die Standardvorgabe zurücksetzen" gibt es in der Oberfläche
 * nicht: der Katalog-Default ist nur ein Startwert, und hat eine Kanzlei sich
 * für eine Einstellung entschieden, ist die Rückkehr zu unserer Vorgabe kein
 * sinnvolles Ziel. Der DELETE-Endpunkt existiert am Backend weiterhin
 * (generisches CRUD), falls das jemals gebraucht wird.
 */

export const useCreateHealthCheckRuleConfigType = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createHealthCheckRuleConfigType, isPending } =
    useMutation({
      mutationFn: (entry: {
        idHealthCheckRuleConfig: number;
        idLegalTransactionType: number;
      }) =>
        customFetcher({
          endpoint: '/healthcheckruleconfigtype',
          method: 'POST',
          body: entry,
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [CONFIG_TYPE_KEY] });
      },
    });

  return {
    createHealthCheckRuleConfigType,
    isPendingCreateHealthCheckRuleConfigType: isPending,
  };
};

export const useDeleteHealthCheckRuleConfigType = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteHealthCheckRuleConfigType, isPending } =
    useMutation({
      mutationFn: (idHealthCheckRuleConfigType: number) =>
        customFetcher({
          endpoint: `/healthcheckruleconfigtype/${idHealthCheckRuleConfigType}`,
          method: 'DELETE',
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [CONFIG_TYPE_KEY] });
      },
    });

  return {
    deleteHealthCheckRuleConfigType,
    isPendingDeleteHealthCheckRuleConfigType: isPending,
  };
};
